import { authenticatedFetch } from './authApi';
import { getEnv } from '../utils/env';

const REPORT_INTERVAL_MS = 15_000;
const MAX_EVENT_AGE_MS = 60_000;
const inputEvents = ['pointerdown', 'keydown', 'input', 'wheel', 'touchmove'];

export async function reportPortalActivity(ageMs: number, signal: AbortSignal): Promise<void> {
  const baseUrl = getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
  if (!baseUrl) return;
  const response = await authenticatedFetch(`${baseUrl}/api/v1/stream/global/activity`, {
    method: 'POST', signal, body: JSON.stringify({ age_ms: ageMs }),
  });
  if (!response.ok) throw new Error('Activity reporting unavailable');
}

/** Observe input only: timers, stream heartbeats and programmatic events are not activity. */
export function startPortalActivityTracking({
  target = document, now = () => performance.now(), report = reportPortalActivity,
}: {
  target?: Document; now?: () => number;
  report?: (ageMs: number, signal: AbortSignal) => Promise<void>;
} = {}): () => void {
  const controller = new AbortController();
  let latestInput: number | null = null;
  let lastSent = -Infinity;
  let inFlight = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => {
    timer = undefined;
    if (controller.signal.aborted || inFlight || latestInput === null) return;
    const elapsed = now() - lastSent;
    if (elapsed < REPORT_INTERVAL_MS) {
      timer = setTimeout(flush, REPORT_INTERVAL_MS - elapsed);
      return;
    }
    const ageMs = Math.max(0, Math.round(now() - latestInput));
    latestInput = null;
    // A suspended background tab must not report old input as a new action.
    if (ageMs > MAX_EVENT_AGE_MS) return;
    lastSent = now();
    inFlight = true;
    void report(ageMs, controller.signal).catch(() => {
      // Best effort; the next actual interaction may retry, without an idle loop.
    }).finally(() => {
      inFlight = false;
      if (!controller.signal.aborted && latestInput !== null) flush();
    });
  };
  const onInput = (event: Event) => {
    if (!event.isTrusted || target.visibilityState !== 'visible' || controller.signal.aborted) return;
    latestInput = now();
    if (!timer && !inFlight) flush();
  };
  inputEvents.forEach(name => target.addEventListener(name, onInput, { capture: true, passive: true }));
  return () => {
    controller.abort();
    if (timer) clearTimeout(timer);
    inputEvents.forEach(name => target.removeEventListener(name, onInput, true));
  };
}
